import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, ArrowRight, Loader2, X } from 'lucide-react';

/**
 * Catch-Up Missions — Teacher drowning alerts card (CUM-4).
 *
 * Surfaces unread `mission.drowning_alert` entries scoped to the teacher's
 * class assignments. Each alert can be dismissed (mark-read) which animates
 * it out. Auto-polls every 60s.
 */
export default function DrowningAlertCard() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissing, setDismissing] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/teacher/me/alerts/drowning');
      setAlerts(data?.alerts || []);
    } catch (e) {
      console.warn('[DrowningAlertCard] load failed:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 60000);
    return () => clearInterval(pollRef.current);
  }, [load]);

  const dismiss = async (alertId) => {
    setDismissing(alertId);
    try {
      await api.post(`/teacher/me/alerts/drowning/${alertId}/mark-read`);
      setAlerts((prev) => prev.filter((a) => a.action_id !== alertId));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to dismiss');
      setDismissing(null);
    }
  };

  if (loading) return null;
  if (!alerts || alerts.length === 0) return null;

  return (
    <section
      className="bg-white border border-amber-300 rounded-xl p-5 shadow-sm"
      data-testid="drowning-alert-card"
    >
      <header className="flex items-center gap-2 mb-3">
        <AlertTriangle size={16} className="text-amber-600" />
        <h3 className="text-sm font-semibold text-slate-900">
          {alerts.length} student{alerts.length === 1 ? '' : 's'} may need a 1:1
        </h3>
        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 text-[10px]">
          {alerts.length}
        </Badge>
      </header>

      <ul className="space-y-2" data-testid="drowning-alert-list">
        {alerts.map((alert) => {
          const ratio = Math.round((alert.ratio || 0) * 100);
          const ts = alert.created_at ? new Date(alert.created_at) : null;
          const ageMs = ts ? Date.now() - ts.getTime() : null;
          const ageLabel = ageMs == null ? '' : prettyAge(ageMs);
          return (
            <li
              key={alert.action_id}
              className="flex items-start gap-3 border rounded-lg p-3 bg-amber-50/30 border-amber-200"
              data-testid={`drowning-alert-${alert.action_id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900">
                  {alert.weak_topic_count} of {alert.total_topic_count} topics flagged
                  <span className="text-slate-500 font-normal"> · {ratio}% weak</span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {ageLabel}
                  {alert.assessment_id && (
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => navigate(`/school-teacher/attempts/${alert.attempt_id}`)}
                      className="px-1 h-auto text-xs"
                      data-testid={`drowning-alert-open-${alert.action_id}`}
                    >
                      Open attempt <ArrowRight size={11} className="ml-0.5" />
                    </Button>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => dismiss(alert.action_id)}
                disabled={dismissing === alert.action_id}
                aria-label="Dismiss"
                className="text-slate-400 hover:text-rose-600 shrink-0"
                data-testid={`drowning-alert-dismiss-${alert.action_id}`}
              >
                {dismissing === alert.action_id ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />}
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function prettyAge(ms) {
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins || 1} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}
